import { Component, OnInit } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { first } from 'rxjs/operators';

import { AutoSub, AutoSubOnDestroy, ProductService, VoucherService, RestService, ProductVoucherList } from '../../providers';
import { Product, Voucher, ProductType } from '../../models';

class ProductTotal {
    count: number;
    price: number;
    product: Product;
    redeemedCount: number;
}

@AutoSubOnDestroy()
@Component({
    selector: 'voucher-manager',
    templateUrl: './voucher-manager.component.html',
    styleUrls: ['./voucher-manager.component.scss']
})
export class VoucherManagerComponent implements OnInit {

    public products: Product[];
    public totalList: ProductTotal[] = [];
    public productMap: { [producKey: string]: Product } = {};
    public voucherParents: Voucher[] = [];
    public voucherChildren: ProductVoucherList;
    public parentMap: { [parentKey: string]: Voucher[] } = {};

    public addVoucher: Voucher = new Voucher();
    public addAmount: number = 0;
    public isCreatingVoucher: boolean = false;
    public isSendingInvoice: boolean = false;

    public partnerId: string;
    public partnerEmail: string;
    public invoiceStart: Date;
    public invoiceEnd: Date;
    public today: Date;
    public errorText = "";

    // Query start date
    public dayCount = 3;
    public startDate: Date = new Date();

    // Stats
    public redeemedCount: number = 0;
    public purchasedCount: number = 0;
    public purchasedPrice: number = 0;
    public freeTicketCount: number = 0;

    private initSubject: ReplaySubject<void> = new ReplaySubject<void>();

    public showCreateVoucher: boolean = false;
    public showIncome: boolean = false;
    public showSendInvoice: boolean = false;
    public showChildren: { [voucherKey: string]: boolean } = {};

    // Expose to view
    public ProductType = ProductType;

    constructor(private voucherService: VoucherService,
        private productService: ProductService,
        private restService: RestService) { }

    ngOnInit() {
        AutoSub(this).reg['productSub'] = this.productService.watchAllProducts().subscribe((products) => {
            products.forEach(product => this.productMap[product.key] = product);
            this.products = products.sort( (prodA, prodB) => prodA.title.localeCompare(prodB.title));
            this.initSubject.next();
        });
        this.updateVoucherQuery();
    }

    private updateVoucherQuery() {
        let now = new Date();

        this.startDate = new Date(now.setDate(now.getDate() - this.dayCount));
        let summerTime = this.startDate.getTimezoneOffset() < -60;
        this.startDate.setHours(summerTime ? 1 : 0, 0, 0, 0);

        AutoSub(this).reg['voucherSub'] = this.voucherService.watchVouchers(this.startDate.getTime()).subscribe(vouchers => {
            // Sort vouchers according to createdOn
            vouchers = vouchers.sort((a, b) => b.createdOn - a.createdOn);
            // Separate vouchers into parents and children
            this.voucherParents = vouchers.filter((voucher) => !voucher.parent)
            let voucherChildren = vouchers.filter((voucher) => voucher.parent);
            // Fill parent map
            this.parentMap = {};
            voucherChildren.forEach((child) => {
                if(!this.parentMap[child.parent]) this.parentMap[child.parent] = [];
                this.parentMap[child.parent].push(child);
            });
            this.updateVoucherStats(this.voucherParents);
        });
    }

    private async updateVoucherStats(vouchers: Voucher[]) {
        // Await init of project map
        await this.initSubject.pipe(first()).toPromise();

        this.purchasedPrice = 0;
        this.purchasedCount = 0;
        this.freeTicketCount = 0;
        this.redeemedCount = 0;

        // Init
        let totalMap: { [productKey: string]: ProductTotal } = {};
        this.products.forEach(product => totalMap[product.key] = { count: 0, price: 0, product: product, redeemedCount: 0 });

        for(let voucher of vouchers) {
            let product = this.productMap[voucher.productKey];

            if(!product) {
                console.warn("No product for voucher, skip adding price", voucher);
                continue;
            }

            if(voucher.isFreeTicket || typeof product.price != 'number') {
                this.freeTicketCount++;
                continue;
            }

            this.purchasedCount++;
            this.purchasedPrice += product.price;
            totalMap[product.key].price += product.price;

            totalMap[product.key].count++;
            if(voucher.redeemedOn) {
                totalMap[product.key].redeemedCount++;
                this.redeemedCount++;
            }
        }

        this.totalList = Object.keys(totalMap).map(key => totalMap[key]);
    }

    public updateList() {
        this.updateVoucherQuery();
    }

    public createVoucher() {
        this.showCreateVoucher = true;
    }

    public cancelCreateVoucher() {
        this.showCreateVoucher = false;
        this.isCreatingVoucher = false;
    }

    public async saveVoucher() {
        this.isCreatingVoucher = true;
        let rsp = await this.voucherService.createVoucher(this.addVoucher, this.addAmount);
        console.log("Voucher response ", rsp);
        this.showCreateVoucher = false;
        this.isCreatingVoucher = false;
    }

    public toggleIncome() {
        this.showIncome = !this.showIncome;
    }

    public sendInvoice() {
        this.showSendInvoice = true;
    }

    public cancelSendInvoice() {
        this.showSendInvoice = false;
        this.isSendingInvoice = false;
    }

    public async saveInvoice() {
        // Convert dates to time since epoch
        let invoiceStartTime = this.invoiceStart ? new Date(this.invoiceStart).valueOf() : null;
        let invoiceEndTime = this.invoiceEnd ? new Date(this.invoiceEnd).valueOf() : null;

        this.isSendingInvoice = true;
        let emailResponse = await this.restService.sendPartnerInvoice(this.partnerId, this.partnerEmail, invoiceStartTime, invoiceEndTime);
        console.log("Email response: ", emailResponse);
        this.isSendingInvoice = false;
    }

    public getTodayString() {
        let today = new Date();
        return today.toLocaleDateString('en-CA');
    }

    public trackByKey(index: number, voucher: Voucher): string {
        return voucher.key;
    }

    public toggleShowChildren(voucherKey: string) {
        this.showChildren[voucherKey] = !this.showChildren[voucherKey];
    }
}
